"use client";

import { useState, useCallback } from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import { useTranslations } from "next-intl"


export interface EmailMessageDetail {
  id: string;
  externalId: string;
  threadId: string;
  inboxId?: string;
  snippet: string;
  from: string;
  subject: string;
  date: string;
  receivedAt?: string;
  to?: string;
  headers?: Record<string, string>;
  bodyText?: string;
  bodyHtml?: string;
  overrideIdentity?: string;
  crmExternalUrl?: string | null;
}

export function useEmailMessage() {
  const tUi = useTranslations("Feedback.UseEmailMessage")

  const { fetchWithAuth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(
    async (externalId: string, agentId?: string): Promise<EmailMessageDetail> => {
      setIsLoading(true);
      setError(null);

      try {
        let url = `/api/agents/email/message?external_id=${encodeURIComponent(externalId)}`;
        // Add agent_id for per-agent identity overrides
        if (agentId) {
          url += `&agent_id=${encodeURIComponent(agentId)}`;
        }
        const res = await fetchWithAuth(url);

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || "Failed to load email");
        }

        return res.json();
      } catch (err) {
        const message = err instanceof Error ? err.message : tUi("failedToLoadEmail");
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchWithAuth]
  );

  return {
    fetchDetail,
    isLoading,
    error,
  };
}
